<!--
  Dibuja la primera ruta que encaje.

  `pathname` se puede dar a mano, y ahí está la gracia: el constructor abre sus
  ajustes y su vista previa como capas encima de lo que ya estaba, y para eso
  dibuja su contenido contra la ubicación de fondo en vez de contra la de
  verdad. Ver `RouteState` en `lib/router.svelte.ts`.
-->
<script lang="ts">
  import {
    location,
    matchRoutes,
    navigate,
    routeComponent,
    type RouteDef,
  } from "../lib/router.svelte";
  import Private from "./Private.svelte";
  import ErrorNote from "./ui/ErrorNote.svelte";
  import Loading from "./ui/Loading.svelte";

  let {
    routes,
    pathname,
    fallback,
  }: {
    routes: RouteDef[];
    /** Con que dirección se emparejan las rutas. Por defecto, donde se está. */
    pathname?: string;
    /** A donde ir cuando no encaja ninguna. */
    fallback?: string;
  } = $props();

  const path = $derived(pathname ?? location.pathname);
  const hit = $derived(matchRoutes(routes, path));

  $effect(() => {
    if (hit || !fallback) return;
    // Si el respaldo tampoco encaja no se va a ninguna parte: navegar a una
    // ruta que no existe volveria a caer aquí, y la vuelta no tiene fin.
    if (!matchRoutes(routes, fallback)) return;
    navigate(fallback, { replace: true });
  });
</script>

{#if hit}
  <!--
    Una ruta con `load` llega en su propio archivo la primera vez. Ya traída,
    `routeComponent` la devuelve tal cual y no como promesa, así que esto la
    dibuja al instante y moverse dentro de ella no la vuelve a montar.
  -->
  {#await routeComponent(hit.route)}
    <Loading />
  {:then Page}
    <!-- Otra aplicación necesita su propio estado y contexto; cambiar de página los conserva. -->
    {#key hit.params.appId ?? hit.params.slug}
      {#if hit.route.guarded}
        <Private>
          <Page params={hit.params} rest={hit.rest} />
        </Private>
      {:else}
        <Page params={hit.params} rest={hit.rest} />
      {/if}
    {/key}
  {:catch}
    <div class="router-load-error">
      <ErrorNote
        message="No se pudo cargar esta pantalla. Puede que haya una versión nueva de Planer: recarga la página."
      />
    </div>
  {/await}
{/if}

<style>
  .router-load-error {
    padding: var(--sp-24);
  }
</style>
